import { useState } from 'react';
import PanelHeader from './components/PanelHeader';
import PanelBody from './PanelBody';
import CampaignsSection from './CampaignsSection';
import CatalogSection from './CatalogSection';
import CustomerAnalyticsSection from './CustomerAnalyticsSection';
import DailyChecklistSection from './DailyChecklistSection';
import LeadsPanel from './LeadsPanel';

/**
 * Vista de una marca: header (siempre visible) con el segmentado + la sección
 * activa (Resumen = dashboard por vertical, o Campañas y anuncios). Compartida
 * por el panel admin (con back + selector de marca) y el del cliente. El fetch
 * lo hace el caller y pasa los resultados en `panel` / `campaigns`.
 */
export default function BrandView({
  brand,
  panel,
  campaigns,
  catalog,
  catalogHealth,
  customerAnalytics,
  dailyChecklist,
  leads,
  canSync = true,
  showCampaigns = true,
  glass = false,
  dateControls,
  onBack,
  brandSwitcher,
  previewFetcher,
  priceExplorerFetcher,
  segmentExportFetcher,
}) {
  const [section, setSection] = useState('summary');
  const hasDaily = Boolean(dailyChecklist);
  const hasLeads = Boolean(leads);
  const hasCustomers = Boolean(customerAnalytics) && brand?.vertical === 'ecommerce';
  // Catálogo solo existe para ecom; Daily/Leads solo donde se provee la query;
  // si quedó uno seleccionado al cambiar de contexto, cae a Resumen.
  let active = section;
  if (active === 'catalog' && brand?.vertical !== 'ecommerce') active = 'summary';
  if (active === 'customers' && !hasCustomers) active = 'summary';
  if (active === 'daily' && !hasDaily) active = 'summary';
  if (active === 'leads' && !hasLeads) active = 'summary';
  // lead_gen no tiene "Resumen": el principal es "Leads".
  if (active === 'summary' && hasLeads) active = 'leads';

  return (
    <div className="space-y-6">
      <PanelHeader
        brand={brand}
        updatedAt={campaigns?.data?.updated_at}
        dateControls={dateControls}
        section={active}
        onSectionChange={setSection}
        onBack={onBack}
        brandSwitcher={brandSwitcher}
        showDaily={hasDaily}
        showLeads={hasLeads}
        showCampaigns={showCampaigns}
        showCustomers={hasCustomers}
        glass={glass}
      />

      {active === 'summary' &&
        (panel.isLoading && !panel.data ? (
          <p className="py-12 text-center text-sm text-gray-500">Cargando panel…</p>
        ) : panel.isError || !panel.data ? (
          <p className="py-12 text-center text-sm text-red-600">
            No se pudo cargar el panel. Probá de nuevo en unos segundos.
          </p>
        ) : (
          <PanelBody data={panel.data} previousSummary={panel.previousSummary} />
        ))}

      {active === 'daily' && (
        <DailyChecklistSection
          data={dailyChecklist?.data}
          isLoading={dailyChecklist?.isLoading}
          isError={dailyChecklist?.isError}
        />
      )}

      {active === 'leads' && (
        <LeadsPanel
          brandId={brand?.id}
          data={leads?.data}
          isLoading={leads?.isLoading}
          isError={leads?.isError}
          onSynced={() => leads?.refetch?.()}
          previewFetcher={previewFetcher}
          canSync={canSync}
        />
      )}

      {active === 'customers' && (
        <CustomerAnalyticsSection
          data={customerAnalytics?.data}
          isLoading={customerAnalytics?.isLoading}
          isError={customerAnalytics?.isError}
          exportFetcher={segmentExportFetcher}
        />
      )}

      {active === 'campaigns' && (
        <CampaignsSection
          data={campaigns?.data}
          isLoading={campaigns?.isLoading}
          isError={campaigns?.isError}
          vertical={brand?.vertical}
          previewFetcher={previewFetcher}
        />
      )}

      {active === 'catalog' && (
        <CatalogSection
          brandId={brand?.id}
          data={catalog?.data}
          isLoading={catalog?.isLoading}
          isError={catalog?.isError}
          health={catalogHealth?.data}
          healthLoading={catalogHealth?.isLoading}
          healthError={catalogHealth?.isError}
          priceExplorerFetcher={priceExplorerFetcher}
        />
      )}
    </div>
  );
}
